import React, { useMemo } from "react";
import {
  Box,
  MenuItem,
  Select,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
} from "@mui/material";
import { format } from "date-fns";
import { observer } from "mobx-react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useNavigate } from "react-router-dom";

import GenericInfiniteTable, {
  ColumnDef,
} from "../../../molecules/PatientRxTables/GenericInfiniteTable";
import useStore from "../../../helpers/useStore";
import { RegistryContext } from "../../../stores/Registry/registry.provider";
import { registryKeys } from "../../../stores/Registry/registry.keys";
import { UNSUBMITTED_DOCTOR_REPORT_STATUS } from "../../../stores/Registry/registryStore";
import {
  DoctorReportDto,
  DoctorReportTherapistCountDto,
  GetClinicalDoctorReportsWithFiltersSortOrderEnum,
  GetClinicalDoctorReportsWithFiltersStatusEnum,
} from "../../../api-client";
import { useApi } from "../../../api/ApiProvider";
import { useLocationId } from "../../../stores/User/User.hooks";
import { useAllUsersBasic } from "../../../stores/User/user.queries";
import { PatientsContext } from "../../../stores/Patients/patients.provider";
import { PatientCommands } from "../../../stores/Patients/patient.commands";
import { PATIENT_SUBTABS } from "../../../stores/Patients/patientsStore";
import FlatCard from "../../../atoms/FlatCard";
import { formatDispatchInfo } from "../../PatientDocumentation/doctorReport/dispatchInfo";

const DOCTOR_REPORT_STATUS_VALUES = Object.values(
  GetClinicalDoctorReportsWithFiltersStatusEnum,
);

/**
 * Display label for a therapist row. Always renders the full name in
 * `Nachname, Vorname` form (nickname is intentionally ignored — registry
 * tables are an admin/overview surface where unique identification matters
 * more than personal familiarity).
 */
const formatTherapistName = (u: {
  firstName?: string | null;
  lastName?: string | null;
  nickName?: string | null;
}): string => {
  const last = u.lastName ?? "";
  const first = u.firstName ?? "";
  return `${last}${last && first ? ", " : ""}${first}` || "-";
};

/**
 * Map a doctor-report row's most-recent insurance transaction state to a
 * small set of visual states used in the table.
 *
 *   - `billed`     – an InsuranceTransaction exists for the report's Rx and has
 *                    been processed (billStatus = PREVIOUS).
 *   - `inProgress` – an InsuranceTransaction exists but isn't fully processed
 *                    yet (billStatus = START | OPEN, etc.).
 *   - `unbilled`   – no InsuranceTransaction is linked to the report's Rx yet.
 */
type BillingDisplayState = "billed" | "inProgress" | "unbilled";

const getBillingDisplayState = (
  transactionNumber: string | null | undefined,
  billStatus: string | null | undefined,
): BillingDisplayState => {
  if (!transactionNumber) return "unbilled";
  if (billStatus === "PREVIOUS") return "billed";
  return "inProgress";
};

const billingChipProps: Record<
  BillingDisplayState,
  { label: string; color: "default" | "success" | "warning"; sx: object }
> = {
  billed: {
    label: "Abgerechnet",
    color: "success",
    sx: { bgcolor: "success.light", color: "success.contrastText" },
  },
  inProgress: {
    label: "In Abrechnung",
    color: "warning",
    sx: { bgcolor: "warning.light", color: "warning.contrastText" },
  },
  unbilled: {
    label: "Nicht abgerechnet",
    color: "default",
    sx: { bgcolor: "grey.200", color: "text.secondary" },
  },
};

const formatDoctor = (rx: any): string => {
  const clinic = rx?.clinic;
  const doctor = rx?.doctor;
  const clinicName =
    clinic?.contactInfo?.businessName ?? clinic?.notes ?? clinic?.BSNR ?? "";
  // NB: backend embedded field is `firstNames` (plural) on ContactInfo.
  const doctorFirstNames =
    doctor?.contactInfo?.firstNames ?? doctor?.contactInfo?.firstName;
  const doctorName = doctor
    ? `${doctor?.contactInfo?.lastName ?? ""}${
        doctorFirstNames ? `, ${doctorFirstNames}` : ""
      }`
    : "";
  return [clinicName, doctorName].filter(Boolean).join(" – ") || "-";
};

const RegistryDoctorReportsTab: React.FC = observer(() => {
  const RegistryStore = useStore(RegistryContext);
  const PatientsStore = useStore(PatientsContext);
  const { clinicalDocumentation } = useApi();
  const {
    doctorReportFilters: filters,
    setDoctorReportFilters,
    doctorReportSelectedIndex: selectedIndex,
    setDoctorReportSelectedIndex: setSelectedIndex,
  } = RegistryStore;

  const locationId = useLocationId();
  const navigate = useNavigate();
  const { navigateToPatient } = PatientCommands(PatientsStore);

  const { data: allUsers } = useAllUsersBasic();
  const therapistOptions = useMemo(
    () =>
      (allUsers || []).map((u) => ({
        id: u.id as string,
        label: formatTherapistName({
          firstName: (u as any).firstName,
          lastName: (u as any).lastName,
          nickName: (u as any).nickName,
        }),
      })),
    [allUsers],
  );

  // ---- Effective filters --------------------------------------------------
  const effectiveFilters = useMemo(
    () => ({
      ...filters,
      location: filters.location ?? locationId,
    }),
    [filters, locationId],
  );

  // ---- Therapist counts (summary table above) -----------------------------
  const {
    data: therapistCounts,
    isLoading: isLoadingCounts,
  } = useQuery<DoctorReportTherapistCountDto[]>({
    queryKey: registryKeys.doctorReportTherapistCounts.detail(
      effectiveFilters.location,
      UNSUBMITTED_DOCTOR_REPORT_STATUS,
    ),
    queryFn: () =>
      clinicalDocumentation.getClinicalDoctorReportTherapistCounts(
        UNSUBMITTED_DOCTOR_REPORT_STATUS,
        effectiveFilters.location!,
      ),
    enabled: !!effectiveFilters.location,
    staleTime: moment.duration(2, "minutes").asMilliseconds(),
    meta: { errorMessage: "Therapeut:innen-Zähler konnten nicht geladen werden" },
  });

  // ---- Reports infinite query ---------------------------------------------
  const {
    data,
    isFetchingNextPage,
    isLoading,
    hasNextPage: hasMoreData,
    fetchNextPage,
  } = useInfiniteQuery<DoctorReportDto[], Error>({
    queryKey: registryKeys.doctorReports.filtered(effectiveFilters),
    queryFn: async ({ pageParam = 1 }) => {
      const res = await clinicalDocumentation.getClinicalDoctorReportsWithFilters(
        effectiveFilters.limit,
        pageParam as number,
        GetClinicalDoctorReportsWithFiltersSortOrderEnum.Desc,
        undefined, // userId
        effectiveFilters.assignedToUserId,
        undefined, // patientId
        effectiveFilters.location,
        effectiveFilters.status as
          | GetClinicalDoctorReportsWithFiltersStatusEnum
          | undefined,
        effectiveFilters.doctorBsnr,
        effectiveFilters.accountId,
      );
      return res?.data ?? [];
    },
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length >= effectiveFilters.limit ? allPages.length + 1 : undefined,
    initialPageParam: 1,
    staleTime: moment.duration(2, "minutes").asMilliseconds(),
    enabled: !!effectiveFilters.location,
    meta: { errorMessage: "Therapieberichte konnten nicht geladen werden" },
  });

  const reports: DoctorReportDto[] = data?.pages.flat() ?? [];

  // ---- Cell click → open the exact Rx on the patient page -----------------
  // Navigation is intentionally scoped to the Rezeptnummer / Rezeptdatum
  // cells (not the whole row) so the user can highlight + copy text from
  // every other column. Uses the canonical PatientCommands.navigateToPatient
  // helper so URL projection / store hydration stays consistent with the
  // rest of the app (same pattern used by the Treatment Notes tab).
  const openRxForReport = (report: any) => {
    const patientId = report?.rx?.patient?.id;
    const rxId = report?.rx?.id ?? null;
    if (!patientId) return;
    navigateToPatient(
      navigate,
      patientId,
      rxId,
      rxId ? PATIENT_SUBTABS.PRESCRIPTIONS : undefined,
    );
  };

  // Shared sx for the two Rx-navigation cells. Rendered as a "pill":
  //   - white background + dark text, always legible
  //   - invisible (white-on-white) on the default white row background
  //   - pops as a white capsule against the tinted selected-row background
  // This way the link cell stays readable both at rest and when the row
  // is selected (bordo-tinted), without needing a coloured link text that
  // would fight the app's white-primary theme.
  const rxLinkSx = {
    display: "inline-block",
    px: 1,
    py: 0.25,
    borderRadius: "12px",
    backgroundColor: "white",
    color: "text.primary",
    fontWeight: 500,
    cursor: "pointer",
    transition: "box-shadow 120ms ease, background-color 120ms ease",
    "&:hover": {
      backgroundColor: "white",
      boxShadow: 1,
      textDecoration: "underline",
    },
  } as const;

  // ---- Columns ------------------------------------------------------------
  const columns: ColumnDef<any>[] = useMemo(
    () => [
      {
        key: "createdAt",
        header: "Erstellt am",
        render: (r) => (r.createdAt ? format(new Date(r.createdAt), "dd.MM.yyyy") : "-"),
        sortable: true,
      },
      {
        key: "account",
        header: "Konto",
        render: (r) => r.rx?.account?.name ?? "-",
      },
      {
        key: "rxNumber",
        header: "Rezeptnummer",
        render: (r) => (
          <Box
            component="span"
            sx={rxLinkSx}
            onClick={(e) => {
              e.stopPropagation();
              openRxForReport(r);
            }}
          >
            {r.rx?.rxNumber ?? "-"}
          </Box>
        ),
      },
      {
        key: "rxDate",
        header: "Rezeptdatum",
        render: (r) => (
          <Box
            component="span"
            sx={rxLinkSx}
            onClick={(e) => {
              e.stopPropagation();
              openRxForReport(r);
            }}
          >
            {r.rx?.date ? format(new Date(r.rx.date), "dd.MM.yyyy") : "-"}
          </Box>
        ),
      },
      {
        key: "latestTransactionNumber",
        header: "Rechnungsnummer",
        render: (r) => {
          const state = getBillingDisplayState(
            r.latestTransactionNumber,
            r.latestTransactionBillStatus,
          );
          const chipProps = billingChipProps[state];
          const label =
            state === "unbilled"
              ? chipProps.label
              : r.latestTransactionNumber || chipProps.label;
          return (
            <Chip
              size="small"
              label={label}
              sx={{
                ...chipProps.sx,
                fontWeight: 500,
                maxWidth: 220,
              }}
            />
          );
        },
      },
      {
        key: "status",
        header: "Status",
        render: (r) => r.status ?? "-",
      },
      {
        key: "dispatch",
        header: "Versendet",
        render: (r) => formatDispatchInfo(r) ?? "-",
      },
      {
        key: "patientId",
        header: "Patienten-ID",
        render: (r) => r.rx?.patient?.id ?? "-",
      },
      {
        key: "assignedToUser",
        header: "Zugewiesener Therapeut",
        render: (r) =>
          r.assignedToUser
            ? formatTherapistName({
                firstName: r.assignedToUser.firstName,
                lastName: r.assignedToUser.lastName,
                nickName: r.assignedToUser.nickName,
              })
            : "-",
      },
      {
        key: "doctor",
        header: "Arzt (Praxis – Name)",
        render: (r) => formatDoctor(r.rx),
      },
      {
        key: "bsnr",
        header: "BSNR",
        // Clicking a BSNR value loads it into the filter at the top of the
        // page. The placeholder "-" is not clickable. Mirrors the row-click
        // pattern in the therapist summary table above.
        render: (r) => {
          const bsnr = r.rx?.clinic?.BSNR;
          if (!bsnr) return "-";
          return (
            <Box
              component="span"
              sx={{
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={(e) => {
                e.stopPropagation();
                setDoctorReportFilters({ doctorBsnr: bsnr });
              }}
            >
              {bsnr}
            </Box>
          );
        },
      },
    ],
    [navigate, setDoctorReportFilters],
  );

  // ---- Toolbar ------------------------------------------------------------
  const toolbar = (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", p: 1 }}>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Typography variant="caption">Therapeut</Typography>
        <Select
          size="small"
          value={filters.assignedToUserId ?? ""}
          onChange={(e) =>
            setDoctorReportFilters({
              assignedToUserId: (e.target.value as string) || undefined,
            })
          }
          displayEmpty
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">
            <em>Alle</em>
          </MenuItem>
          {therapistOptions.map((u) => (
            <MenuItem key={u.id} value={u.id}>
              {u.label}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Typography variant="caption">Status</Typography>
        <Select
          size="small"
          value={filters.status ?? ""}
          onChange={(e) =>
            setDoctorReportFilters({ status: (e.target.value as string) || undefined })
          }
          displayEmpty
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">
            <em>Alle</em>
          </MenuItem>
          {DOCTOR_REPORT_STATUS_VALUES.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Typography variant="caption">Arzt BSNR</Typography>
        <TextField
          size="small"
          value={filters.doctorBsnr ?? ""}
          onChange={(e) =>
            setDoctorReportFilters({ doctorBsnr: e.target.value || undefined })
          }
          sx={{ minWidth: 140 }}
        />
      </Box>
    </Box>
  );

  // ---- Combined header card: filters + therapist summary ------------------
  const headerCard = (
    <FlatCard
      title="Therapieberichte"
      subtitle={`Offene Berichte je Therapeut:in – Status: ${UNSUBMITTED_DOCTOR_REPORT_STATUS}`}
      sx={{ mb: 1 }}
      sxContent={{ p: 1.5 }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Filters */}
        {toolbar}

        {/* Therapist summary – sized to fit its content (don't stretch to
            the full width of the FlatCard). */}
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            maxHeight: 200,
            width: "fit-content",
            maxWidth: "100%",
            alignSelf: "flex-start",
          }}
        >
          <Table size="small" stickyHeader sx={{ width: "auto" }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: "white",
                    whiteSpace: "nowrap",
                  }}
                >
                  Therapeut:in
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: "white",
                    whiteSpace: "nowrap",
                  }}
                  align="right"
                >
                  Offene Berichte
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingCounts && (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              )}
              {!isLoadingCounts && (therapistCounts ?? []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    Keine offenen Berichte
                  </TableCell>
                </TableRow>
              )}
              {(therapistCounts ?? []).map((row, i) => (
                <TableRow
                  key={row.userId}
                  sx={{
                    backgroundColor: i % 2 === 0 ? "white" : "grey.100",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    setDoctorReportFilters({ assignedToUserId: row.userId })
                  }
                >
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {formatTherapistName(row)}
                  </TableCell>
                  <TableCell align="right">{row.count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </FlatCard>
  );

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* ---- Reports table (filters + therapist summary live above as toolbar) ---- */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <GenericInfiniteTable<any>
          data={reports}
          columns={columns}
          hasMoreData={!!hasMoreData}
          fetchMoreData={fetchNextPage}
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
          scrollId="registryDoctorReportsScroll"
          toolbar={headerCard}
          selectedIndex={selectedIndex}
          setSelectedIndex={setSelectedIndex}
          sortBy={filters.sortBy as any}
          emptyMessage="Keine Therapieberichte gefunden"
        />
      </Box>
    </Box>
  );
});

export default RegistryDoctorReportsTab;
